import React, { useMemo } from "react";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@digital-twins/design-system/components/ui/tooltip";
import { TextClamp } from "../components/text/TextClamp";
import { FitText } from "../components/text/FitText";
import {
  visibleTextClampFromSchema,
  ladderMaxLines,
} from "../foundations/text-limits";
import { registerSlide } from "./registry";
import { presetSlot } from "../layouts/presetSlots";
import { PlotBox } from "../components/chart-segment/PlotBox";
import { ChartSegmentSelectionScope } from "../components/chart-segment/selection-scope";
import { ChartSegmentTitleSlot } from "../components/chart-segment/ChartSegmentTitleSlot";
import { SlideFrame } from "../layouts/SlideFrame";
import { formatQuadrantAxisLabel } from "../foundations/quadrant-axis-label";
import { getQuadrantMapCombinedLayoutMaxConcepts } from "../foundations/slide-limits";
import { EditableSlideText } from "./EditableSlideText";
import { EditableText } from "./EditableText";
import {
  QUADRANT_SLIDE_FRAME,
  QuadrantPlot,
  QuadrantSlideMap,
  QuadrantSlideMapLabelArgs,
  QuadrantSlideMapPoint,
  quadrantSlidePlotAdapter,
  readQuadrantSlide,
} from "../plots/quadrant";
import type { EditableSlideProps } from "./contracts";
import type { QuadrantMapSlideData } from "./renderer-internal-types";

// legacy reader only: the builder stamps the slide title.
const LEGACY_TITLE = 'Positioning map'

type QuadrantMapSlideProps = QuadrantMapSlideData & EditableSlideProps;

interface ResolvedSidebarItem {
  label: string;
  /**
   * The concept's asset id, when the builder stamped one. Joins this row to its
   * point, card size and tooltip data; a deck stored without it joins by label.
   */
  assetId: string | null;
  /**
   * Position of this row's concept in the payload's `points`, the index an edit
   * of its name writes to. Carried from the payload alignment, never looked up
   * by label: two concepts can share a name.
   */
  sourceIndex: number;
  badgeLabel: string;
  description: string;
  tooltipDescription: string;
  imageUrl: string | null;
  isLeader: boolean;
  /**
   * 1-based place in the distance ordering the rail explains: rank 1 is the concept
   * furthest from the midpoint. Read from the payload's `leaderRank` when the builder
   * stamped it, derived from the plotted coordinates when it did not. This, never row
   * position, decides which nominees survive the capacity ceiling.
   */
  leaderRank: number;
}

interface SidebarRow extends ResolvedSidebarItem {
  /**
   * Whether this row renders its explanation in the rail. Every expanded row is a
   * leader; a leader past the rail's measured capacity folds instead, and its
   * explanation stays reachable in the tooltip.
   */
  isExpanded: boolean;
  /**
   * The copy a folded row's tooltip carries. Empty only when the payload stamped no
   * explanation for this row at all, which is the one case where the row renders no
   * info icon: see the icon's guard in the rail below.
   */
  foldedExplanation: string;
}

/**
 * The map's frame, owned by the plot (`QuadrantSlideMap`): the chart box keeps its
 * aspect ratio.
 */
const CHART_FRAME = QUADRANT_SLIDE_FRAME;
const PLOT_RECT = presetSlot("quadrant-map", "plot").rect!;
const RAIL_RECT = presetSlot("quadrant-map", "rail").rect!;

function normalizeLabel(value: string): string {
  return value.replace(/\s+/g, " ").trim().toLowerCase();
}

/**
 * Normalized label -> index, for labels that occur exactly once. A label two
 * concepts share names neither of them, so a join on it falls back to the
 * payload's index alignment instead of landing on whichever came first.
 */
function uniqueLabelIndex(labels: Array<string>): Map<string, number> {
  const seen = new Map<string, number>();
  const repeated = new Set<string>();
  labels.forEach((label, index) => {
    const normalized = normalizeLabel(label);
    if (!normalized) {
      return;
    }
    if (seen.has(normalized)) {
      repeated.add(normalized);
      return;
    }
    seen.set(normalized, index);
  });
  for (const label of repeated) {
    seen.delete(label);
  }
  return seen;
}

function usableAssetId(value: string | null | undefined): string | null {
  return typeof value === "string" && value.trim() ? value.trim() : null;
}

/**
 * Asset id -> index, for ids that occur exactly once. The builder stamps the
 * analysis's asset id on every point and rail row, so a join on it holds when two
 * concepts share a display label or a label was fitted differently on each side.
 */
function uniqueAssetIdIndex(
  items: Array<{ assetId?: string | null }>,
): Map<string, number> {
  const seen = new Map<string, number>();
  const repeated = new Set<string>();
  items.forEach((item, index) => {
    const assetId = usableAssetId(item.assetId);
    if (!assetId) {
      return;
    }
    if (seen.has(assetId)) {
      repeated.add(assetId);
      return;
    }
    seen.set(assetId, index);
  });
  for (const assetId of repeated) {
    seen.delete(assetId);
  }
  return seen;
}

/**
 * The key a point card is measured under: its asset id, else its label for a deck
 * stored before points carried one (the legacy reader).
 */
function conceptKey(point: { assetId: string | null; label: string }): string {
  return point.assetId ?? point.label;
}

/** Tooltip data for a concept: by asset id when the map carries it, else by label. */
function conceptTooltip(
  tooltipData: QuadrantMapSlideData["tooltipData"],
  assetId: string | null,
  label: string,
): { imageUrl?: string | null; thumbnailUrl?: string | null } | undefined {
  return (assetId ? tooltipData?.[assetId] : undefined) ?? tooltipData?.[label];
}

function hasRenderableActionTitle(value?: string | null): value is string {
  return Boolean(
    value && value.trim() && value.trim().toLowerCase() !== "llmfill",
  );
}

function resolveHeadline(actionTitle: string | undefined): string {
  if (hasRenderableActionTitle(actionTitle)) {
    return actionTitle.trim();
  }
  return "";
}

/**
 * Legacy nomination size only. A deck stored before `isLeader` existed has no flag
 * on any row; {@link resolveLeaderFlags} nominates the top three distance ranks
 * there, and nowhere else. Live payloads nominate every distinct concept, and the
 * measured ceiling in {@link maxExpandedLeaderRows} is the only gate on expansion.
 */
const LEADER_ROW_COUNT = 3;

/**
 * Measured rail capacity, probed live at `/internal/deck-render` (2026-09-17, Chromium,
 * 1280x800 slide, one-line concept names, 205-character explanations):
 *
 *   rail clientHeight 691, minus its 20px top and bottom padding -> 651 usable
 *   headline (FitText h2, capped at 3 lines x 16px min) 58, plus the 20px stack gap
 *   selection footnote (2 lines at 12px/1.45) 37, plus the 20px stack gap
 *   folded row 28 · expanded row 34 + 21 per description line · 16px between rows
 *
 * D14 froze "3 leaders at 3 lines fits n=10 at 675 of 691". It does not: that arithmetic
 * counted rows only. With the headline and the rail's own padding the row budget is 573,
 * not 691, and 3 expanded rows at n=10 measured 631 of 573 — 58px past the rail's content
 * box, clipped with no ellipsis. 2 expanded rows measure 566 of 573.
 *
 * The expanded-row count is capacity-driven, and this ceiling is the only gate. It
 * reads the same itemName ladder the rail's name clamp renders with: names get two
 * lines (+25px on every row, folded or expanded) from four to seven concepts, one
 * line otherwise; descriptions get 5 lines at n<=3 and 3 above. The ceiling this
 * yields (expanded rows / concepts):
 *
 *   n   name  headline  headline+footnote  neither  footnote only
 *   3   1     3         3                  3        3
 *   4   2     4         3                  4        4
 *   5   2     3         2                  4        3
 *   6   2     2         1                  3        2
 *   7   2     1         0                  2        1
 *   8   1     3         2                  4        3
 *   9   1     2         1                  3        3
 *   10  1     2         1                  3        2
 *
 * A two-line name therefore costs expanded rows at n=5..7 (with a headline and no
 * footnote, 5 -> 3, 4 -> 2 and 3 -> 1 against one-line names): those rows fold
 * rather than clip, and a folded row keeps its explanation in the tooltip. Names
 * stay one line at n>=8 because even every row folded does not fit two-line names
 * there: 8 x 53 + 7 x 16 = 536 overflows the 516px row budget with a headline and
 * footnote, and 9 x 53 + 8 x 16 = 605 overflows 573 with a headline alone. Names
 * stay one line at n<=3, where descriptions are five lines: a second name line plus
 * a footnote would fold one of three rows.
 *
 * If the rail ever grows, this under-expands (safe). If it shrinks, it over-expands, so
 * re-probe these constants when the rail geometry or the slide height moves.
 */
const RAIL_CONTENT_HEIGHT_PX = 651;
const RAIL_STACK_GAP_PX = 20;
const RAIL_HEADLINE_MAX_HEIGHT_PX = 58;
const RAIL_FOOTNOTE_MAX_HEIGHT_PX = 37;
const SIDEBAR_ROW_GAP_PX = 16;
const FOLDED_ROW_HEIGHT_PX = 28;
const EXPANDED_ROW_BASE_HEIGHT_PX = 34;
const DESCRIPTION_LINE_HEIGHT_PX = 21;
const DESCRIPTION_DESCENDER_BUFFER_PX = 2;
/** Schema's measured cost of one extra concept-name line, on every row. */
const NAME_EXTRA_LINE_PX = 25;

function foldedRowHeight(nameMaxLines: number): number {
  return FOLDED_ROW_HEIGHT_PX + Math.max(0, nameMaxLines - 1) * NAME_EXTRA_LINE_PX;
}

function expandedRowHeight(
  descriptionMaxLines: number,
  nameMaxLines: number,
): number {
  return (
    EXPANDED_ROW_BASE_HEIGHT_PX +
    Math.max(0, nameMaxLines - 1) * NAME_EXTRA_LINE_PX +
    descriptionMaxLines * DESCRIPTION_LINE_HEIGHT_PX +
    DESCRIPTION_DESCENDER_BUFFER_PX
  );
}

/**
 * How many nominated rows can render expanded prose without pushing the rail past
 * its content box. The loop's early break is the measurement. Which rows are
 * nominated stays the payload's call; this only counts how many fit.
 */
function maxExpandedLeaderRows(params: {
  itemCount: number;
  descriptionMaxLines: number;
  nameMaxLines: number;
  hasHeadline: boolean;
  hasFootnote: boolean;
}): number {
  const {
    itemCount,
    descriptionMaxLines,
    nameMaxLines,
    hasHeadline,
    hasFootnote,
  } = params;
  const rowsBudget =
    RAIL_CONTENT_HEIGHT_PX -
    (hasHeadline ? RAIL_HEADLINE_MAX_HEIGHT_PX + RAIL_STACK_GAP_PX : 0) -
    (hasFootnote ? RAIL_FOOTNOTE_MAX_HEIGHT_PX + RAIL_STACK_GAP_PX : 0);
  const gapsHeight = Math.max(0, itemCount - 1) * SIDEBAR_ROW_GAP_PX;
  const expandedHeight = expandedRowHeight(descriptionMaxLines, nameMaxLines);
  const foldedHeight = foldedRowHeight(nameMaxLines);

  let allowed = 0;
  for (let candidate = 1; candidate <= itemCount; candidate += 1) {
    const cost =
      gapsHeight +
      candidate * expandedHeight +
      (itemCount - candidate) * foldedHeight;
    if (cost > rowsBudget) {
      break;
    }
    allowed = candidate;
  }
  return allowed;
}

/**
 * D19: `TooltipContent` carries no max-width in the design system's class list, so a
 * 215-character explanation in it renders as one absurdly wide line. 320px sits under
 * the rail's measured 379px description column, which keeps the folded row's text at a
 * line length the reader already scans in the rail itself.
 */
const FOLDED_TOOLTIP_MAX_WIDTH_PX = 320;
const FOLDED_TOOLTIP_MAX_WIDTH_CLASS = "max-w-[320px]";

interface LeaderRankCandidate {
  /** The payload's `leaderRank`, already validated; null when the deck predates it. */
  stampedRank: number | null;
  x: number | null;
  y: number | null;
  label: string;
  index: number;
}

function usableStampedRank(value: number | null | undefined): number | null {
  if (typeof value !== "number" || !Number.isFinite(value) || value < 1) {
    return null;
  }
  return Math.trunc(value);
}

/**
 * Inverse of the study's position letters. `A` is 0, `Z` is 25, `AA` is 26.
 * Comparing the letter strings is wrong, because `AA` is emitted after `Z`
 * and `"AA" < "B"`. Mirrors `canonical_deck.slides.quadrant_map._badge_letter_index`.
 */
function badgeLetterIndex(letter: string): number | null {
  const text = letter.trim().toUpperCase();
  if (!text || Array.from(text).some((ch) => ch < "A" || ch > "Z")) {
    return null;
  }
  let index = 0;
  for (const ch of text) {
    index = index * 26 + (ch.charCodeAt(0) - "A".charCodeAt(0) + 1);
  }
  return index - 1;
}

function compareBadgeOrder(
  badgeA: string | null | undefined,
  badgeB: string | null | undefined,
  indexA: number,
  indexB: number,
): number {
  const rankA = badgeA ? badgeLetterIndex(badgeA) : null;
  const rankB = badgeB ? badgeLetterIndex(badgeB) : null;
  if (rankA !== null && rankB !== null && rankA !== rankB) {
    return rankA - rankB;
  }
  if (rankA !== null && rankB === null) {
    return -1;
  }
  if (rankA === null && rankB !== null) {
    return 1;
  }
  return indexA - indexB;
}

/**
 * Rail order is the study badge letter. A deck stored before that sort, or
 * built by a path that still emits asset-id order, arrives as B, A, D, C.
 * Sorting here is what the reader sees; the builder's sort is what a rebuild
 * persists. Rows with no position letter stay in payload order, after the
 * lettered ones. Points stay paired with their sidebar row so the index
 * fallback in {@link resolveRowPoint} still names the same concept.
 */
function orderRailByBadge<
  TPoint,
  TSidebar extends { badgeLabel?: string | null },
>(
  points: Array<TPoint>,
  sidebarItems: Array<TSidebar>,
): { points: Array<TPoint>; sidebarItems: Array<TSidebar> } {
  if (sidebarItems.length === 0) {
    return { points, sidebarItems };
  }
  const pairCount = Math.min(points.length, sidebarItems.length);
  const paired = Array.from({ length: pairCount }, (_, index) => index);
  paired.sort((a, b) =>
    compareBadgeOrder(
      sidebarItems[a]?.badgeLabel,
      sidebarItems[b]?.badgeLabel,
      a,
      b,
    ),
  );
  return {
    points: [
      ...paired.map((index) => points[index]),
      ...points.slice(pairCount),
    ],
    sidebarItems: [
      ...paired.map((index) => sidebarItems[index]),
      ...sidebarItems.slice(pairCount),
    ],
  };
}

/**
 * Where a rail row sits on the map. The row's asset id names its point when both carry
 * one. A deck stored without it (the legacy reader) joins by normalized label, a label
 * that names exactly one point first, the index alignment otherwise (two concepts
 * sharing a name never borrow each other's position). Null when neither yields finite
 * coordinates, which ranks the row at the midpoint (the builder's own fallback).
 */
function resolveRowPoint(
  points: Array<{ label?: string; x?: number; y?: number; assetId?: string | null }>,
  label: string,
  index: number,
  assetId: string | null = null,
): { x: number | null; y: number | null } {
  const assetIndex = assetId ? uniqueAssetIdIndex(points).get(assetId) : undefined;
  const labelIndex = uniqueLabelIndex(
    points.map((point) => point.label || ""),
  ).get(normalizeLabel(label));
  const candidate =
    assetIndex !== undefined
      ? points[assetIndex]
      : labelIndex !== undefined
        ? points[labelIndex]
        : points[index];
  const x = Number(candidate?.x);
  const y = Number(candidate?.y);
  if (!Number.isFinite(x) || !Number.isFinite(y)) {
    return { x: null, y: null };
  }
  return { x, y };
}

function candidateDistance(
  candidate: LeaderRankCandidate,
  xMid: number,
  yMid: number,
): number {
  const x = candidate.x === null ? xMid : candidate.x;
  const y = candidate.y === null ? yMid : candidate.y;
  return Math.hypot(x - xMid, y - yMid);
}

/**
 * H1: the 1-based distance ordering the rail's capacity ceiling demotes by.
 *
 * `leaderRank` (schema 1.4.0) is authoritative and is stamped on every row. Payload
 * order is NOT a proxy for it: `sidebarItems` arrives in the analysis's `sorted()` by
 * asset id, so before this existed a ceiling that expanded 2 of 3 nominees kept
 * whichever two sorted first alphabetically, which could fold the single furthest
 * concept while two nearer ones expanded. That contradicted both the schema and the
 * sentence the deck writes into `mapSelection.rationale`.
 *
 * Legacy path (the review's medium finding): a deck stored before the rank existed has
 * `points[].x/y` and `midpoints` all the same, so the ordering is derived here instead
 * of falling back to asset-id order. The rule mirrors the builder's `_leader_asset_ids`
 * exactly (apps/python-api/canonical_deck/slides/quadrant_map.py): furthest from the
 * midpoint first, ties broken by x descending, then y descending, then label ascending.
 * A row the payload never plotted counts as sitting on the midpoint, so it ranks last,
 * which is that function's `_as_float(..., mid_x)` fallback in the same words.
 *
 * Partially stamped payloads keep the stamped rows in their stamped order and rank the
 * rest behind them by distance. The result is a total order either way, so the same
 * payload always produces the same rail (no `getRandomPersonaIds`-style redraw).
 */
function resolveLeaderRanks(
  candidates: Array<LeaderRankCandidate>,
  xMid: number,
  yMid: number,
): Array<number> {
  const ordered = candidates.slice().sort((a, b) => {
    const rankA = a.stampedRank ?? Number.POSITIVE_INFINITY;
    const rankB = b.stampedRank ?? Number.POSITIVE_INFINITY;
    if (rankA !== rankB) {
      return rankA - rankB;
    }
    const distanceA = candidateDistance(a, xMid, yMid);
    const distanceB = candidateDistance(b, xMid, yMid);
    if (distanceA !== distanceB) {
      return distanceB - distanceA;
    }
    const xA = a.x === null ? xMid : a.x;
    const xB = b.x === null ? xMid : b.x;
    if (xA !== xB) {
      return xB - xA;
    }
    const yA = a.y === null ? yMid : a.y;
    const yB = b.y === null ? yMid : b.y;
    if (yA !== yB) {
      return yB - yA;
    }
    if (a.label !== b.label) {
      return a.label < b.label ? -1 : 1;
    }
    return a.index - b.index;
  });

  const ranks: Array<number> = candidates.map(() => 0);
  ordered.forEach((candidate, position) => {
    ranks[candidate.index] = position + 1;
  });
  return ranks;
}

/**
 * D15: `isLeader` is stamped by the deck builder from a deterministic rule (top 3 by
 * distance from the midpoint, ties broken x, then y, then label), so one study always
 * produces the same rail. The renderer honours the flag and never derives it: leaders
 * are read from the payload, not from row position.
 *
 * It is a nomination rather than a guarantee (schema 1.4.0): the rail's measured ceiling
 * may expand fewer, and the tie is broken by {@link resolveLeaderRanks}, never by which
 * row came first in the payload.
 *
 * Legacy path: a deck persisted before the flag existed carries it on no row at all.
 * There, and only there, the nominees are the top {@link LEADER_ROW_COUNT} rows of the
 * distance ordering — the ordering, not the payload's asset-id order, because the rail
 * claims in prose that it explains the concepts furthest from the midpoint.
 */
function resolveLeaderFlags(
  stampedFlags: Array<boolean | null | undefined>,
  ranks: Array<number>,
): Array<boolean> {
  const hasStampedFlag = stampedFlags.some(
    (flag) => typeof flag === "boolean",
  );
  if (hasStampedFlag) {
    return stampedFlags.map((flag) => flag === true);
  }
  return stampedFlags.map((_flag, index) => ranks[index] <= LEADER_ROW_COUNT);
}

/**
 * The folded row's whole affordance: the info icon is the tooltip trigger, and the
 * tooltip is where the full concept name (cut to one line in the rail, D16) and the
 * full explanation stay reachable. A button rather than a span so the trigger is
 * focusable, which is also how the tooltip opens without a pointer.
 */
function FoldedRowInfoTooltip(props: {
  label: string;
  description: string;
  badgeLabel: string;
}) {
  const { label, description, badgeLabel } = props;

  return (
    <TooltipProvider>
      <Tooltip delayDuration={300}>
        <TooltipTrigger asChild>
          <button
            type="button"
            data-testid={`quadrant-map-sidebar-info-${badgeLabel}`}
            aria-label={label}
            style={{
              display: "inline-flex",
              alignItems: "center",
              justifyContent: "center",
              flexShrink: 0,
              width: "16px",
              height: "16px",
              padding: 0,
              border: "none",
              background: "none",
              color: "var(--ds-text-muted)",
              cursor: "help",
              lineHeight: 0,
            }}
          >
            <svg
              width="14"
              height="14"
              viewBox="0 0 16 16"
              fill="none"
              stroke="currentColor"
              strokeWidth="1.4"
              aria-hidden="true"
              focusable="false"
            >
              <circle cx="8" cy="8" r="6.6" />
              <path d="M8 7.1v4" strokeLinecap="round" />
              <path d="M8 4.7h.01" strokeLinecap="round" />
            </svg>
          </button>
        </TooltipTrigger>
        <TooltipContent
          className={FOLDED_TOOLTIP_MAX_WIDTH_CLASS}
          data-testid={`quadrant-map-sidebar-tooltip-${badgeLabel}`}
          side="left"
          // Both the class and the inline cap, deliberately: the class is the design
          // system's own idiom, and the inline value holds even in a consumer whose
          // Tailwind content globs do not reach this package.
          style={{ maxWidth: `${FOLDED_TOOLTIP_MAX_WIDTH_PX}px` }}
        >
          <span
            style={{
              display: "block",
              fontWeight: "var(--ds-weight-medium)",
            }}
          >
            {label}
          </span>
          {description ? (
            <span style={{ display: "block", marginTop: "2px" }}>
              {description}
            </span>
          ) : null}
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
}

const BADGE_STYLES = {
  sidebar: {
    background: "var(--ds-white, #fff)",
    border: "1px solid var(--ds-quadrant-badge-border)",
    color: "var(--ds-quadrant-legend-badge-fg)",
  },
} as const;

/**
 * The legacy reader for the map (plots Wave 7 D1; named in
 * docs/deck-design-system/plots/read-adapters.md): a slide stored before
 * `plot` was stamped draws exactly as it did. Its points, after the rail's
 * badge order and the display cap, joined to their rail row by asset id, else
 * by a label that names exactly one row, else by index; a point without finite
 * coordinates or a name is not drawn. The badge and image come from the rail
 * row (the image falls back to the tooltip data), as they always have.
 */
function readLegacyQuadrantMapPoints(params: {
  displayPoints: QuadrantMapSlideData["points"];
  resolvedSidebarItems: Array<ResolvedSidebarItem>;
  sidebarAssetIndex: Map<string, number>;
  sidebarLabelIndex: Map<string, number>;
  tooltipData: QuadrantMapSlideData["tooltipData"];
  sourceIndexOf: (point: unknown, fallback: number) => number;
}): Array<QuadrantSlideMapPoint> {
  const {
    displayPoints,
    resolvedSidebarItems,
    sidebarAssetIndex,
    sidebarLabelIndex,
    tooltipData,
    sourceIndexOf,
  } = params;
  return (displayPoints || [])
    .map((point, index) => {
      const x = Number(point.x);
      const y = Number(point.y);
      if (!Number.isFinite(x) || !Number.isFinite(y)) {
        return null;
      }
      const label = (point.label || "").replace(/\s+/g, " ").trim();
      if (!label) {
        return null;
      }
      const assetId = usableAssetId(point.assetId);
      const assetIndex = assetId ? sidebarAssetIndex.get(assetId) : undefined;
      const labelIndex = sidebarLabelIndex.get(normalizeLabel(label));
      const sidebarItem =
        resolvedSidebarItems[
          assetIndex !== undefined
            ? assetIndex
            : labelIndex !== undefined
              ? labelIndex
              : index
        ];
      const tooltip = conceptTooltip(tooltipData, assetId, label);
      return {
        key: conceptKey({ assetId, label }),
        label,
        sourceIndex: sourceIndexOf(point, index),
        x,
        y,
        badgeLabel:
          sidebarItem?.badgeLabel || String.fromCharCode(65 + index),
        imageUrl:
          sidebarItem?.imageUrl ||
          tooltip?.thumbnailUrl ||
          tooltip?.imageUrl ||
          null,
      };
    })
    .filter(
      (point): point is Exclude<typeof point, null> => point !== null,
    );
}

function QuadrantMapSlideComponent(props: QuadrantMapSlideProps) {
  const {
    eyebrow,
    footerText,
    clientLogoUrl,
    actionTitle,
    points = [],
    axisLabels,
    quadrantLabels,
    midpoints,
    sidebarItems,
    selectionFootnote,
    tooltipData,
    editable,
    onEdit,
    onEditFields,
    renderMode = "interactive",
  } = props;

  /**
   * D17: the deck is rendered server side through a headless browser into a PDF
   * (`InternalDeckRenderPage`, which passes `renderMode: 'static'`). No pointer and no
   * focus ever arrive there, so a tooltip never opens. The export therefore prints only
   * what is visible — the expanded leader rows — and nothing for a folded row: the info
   * icon goes too, because an icon in a static export advertises content the reader
   * cannot reach.
   *
   * It follows that a static export shows less than the app does. That is the honest
   * outcome and the product call; the alternative was a trigger that does nothing.
   */
  const isStaticRender = renderMode === "static";

  const xMid = midpoints?.x ?? 50;
  const yMid = midpoints?.y ?? 50;
  const slideTypeForLimits = "quadrant-map";
  const itemNameClamp = visibleTextClampFromSchema(
    "itemName",
    slideTypeForLimits,
    {
      lineHeight: "1.4em",
    },
  );
  const descriptionClamp = visibleTextClampFromSchema(
    "description",
    slideTypeForLimits,
    {
      lineHeight: "1.5em",
    },
  );
  const selectionFootnoteClamp = visibleTextClampFromSchema(
    "selectionFootnote",
    slideTypeForLimits,
    {
      lineHeight: "1.45em",
    },
  );
  const displayCap =
    getQuadrantMapCombinedLayoutMaxConcepts(slideTypeForLimits);
  const orderedRail = orderRailByBadge(points || [], sidebarItems || []);
  // The payload index a displayed concept came from: the rail order and the cap
  // move rows, and an edit must still write to the concept that was shown.
  const sourceIndexOf = (point: unknown, fallback: number): number => {
    const index = (points || []).indexOf(point as (typeof points)[number]);
    return index >= 0 ? index : fallback;
  };
  const cappedPoints = orderedRail.points.slice(0, displayCap);
  const cappedSidebar = orderedRail.sidebarItems.slice(0, displayCap);
  const alignedLen =
    cappedPoints.length > 0 && cappedSidebar.length > 0
      ? Math.min(cappedPoints.length, cappedSidebar.length)
      : null;
  const displayPoints =
    alignedLen !== null ? cappedPoints.slice(0, alignedLen) : cappedPoints;
  const displaySidebarItems =
    alignedLen !== null ? cappedSidebar.slice(0, alignedLen) : cappedSidebar;
  const resolvedSidebarItems = useMemo(() => {
    if (displaySidebarItems.length > 0) {
      const leaderRanks = resolveLeaderRanks(
        displaySidebarItems.map((item, index) => {
          const point = resolveRowPoint(
            displayPoints,
            item.label || "",
            index,
            usableAssetId(item.assetId),
          );
          return {
            stampedRank: usableStampedRank(item.leaderRank),
            x: point.x,
            y: point.y,
            label: (item.label || "").replace(/\s+/g, " ").trim(),
            index,
          };
        }),
        xMid,
        yMid,
      );
      const leaderFlags = resolveLeaderFlags(
        displaySidebarItems.map((item) => item.isLeader),
        leaderRanks,
      );
      const pointAssetIndex = uniqueAssetIdIndex(displayPoints);
      return displaySidebarItems.map((item, index) => {
        const label = (item.label || "").replace(/\s+/g, " ").trim();
        const assetId = usableAssetId(item.assetId);
        const pointIndex = assetId ? pointAssetIndex.get(assetId) : undefined;
        const tooltip = conceptTooltip(tooltipData, assetId, item.label || "");
        return {
        label,
        assetId,
        sourceIndex: sourceIndexOf(
          displayPoints[pointIndex !== undefined ? pointIndex : index],
          index,
        ),
        badgeLabel: (item.badgeLabel || String.fromCharCode(65 + index)).trim(),
        description: (item.description || "").replace(/\s+/g, " ").trim(),
        tooltipDescription: (
          item.tooltipDescription ||
          item.description ||
          ""
        ).replace(/\s+/g, " ").trim(),
        imageUrl:
          item.imageUrl ||
          tooltip?.thumbnailUrl ||
          tooltip?.imageUrl ||
          null,
        isLeader: leaderFlags[index],
        leaderRank: leaderRanks[index],
        };
      });
    }

    // `points` carries no `isLeader`, so this branch is always the legacy path:
    // the flags are resolved after the filter so they index the rows that render.
    const pointRows = displayPoints
      .map((point, index) => {
        const label = (point.label || "").replace(/\s+/g, " ").trim();
        if (!label) {
          return null;
        }
        const x = Number(point.x);
        const y = Number(point.y);
        const assetId = usableAssetId(point.assetId);
        const tooltip = conceptTooltip(tooltipData, assetId, label);
        return {
          label,
          assetId,
          sourceIndex: sourceIndexOf(point, index),
          badgeLabel: String.fromCharCode(65 + index),
          description: '',
          tooltipDescription: '',
          imageUrl:
            tooltip?.thumbnailUrl ||
            tooltip?.imageUrl ||
            null,
          // Carried only as far as the ranking below, then dropped: a rail row's
          // shape stays the resolved sidebar item's.
          rankX: Number.isFinite(x) ? x : null,
          rankY: Number.isFinite(y) ? y : null,
        };
      })
      .filter(
        (item): item is Exclude<typeof item, null> => item !== null,
      );

    const pointRanks = resolveLeaderRanks(
      pointRows.map((row, index) => ({
        stampedRank: null,
        x: row.rankX,
        y: row.rankY,
        label: row.label,
        index,
      })),
      xMid,
      yMid,
    );
    const pointLeaderFlags = resolveLeaderFlags(
      pointRows.map(() => undefined),
      pointRanks,
    );
    return pointRows.map((item, index) => {
      const { rankX: _rankX, rankY: _rankY, ...row } = item;
      return {
        ...row,
        isLeader: pointLeaderFlags[index],
        leaderRank: pointRanks[index],
      };
    });
  }, [
    displayPoints,
    displaySidebarItems,
    quadrantLabels,
    tooltipData,
    xMid,
    yMid,
  ]);

  // D2/D4: item count for the description ladder is the number of sidebar rows
  // actually rendered (post cap/alignment), not the raw `points`/`sidebarItems`
  // payload length. `ladderMaxLines` shrinks the per-row description budget as
  // density rises; combined with the schema clamp so neither can widen the other.
  //
  // D18: the ladder now budgets LEADER rows only, and has two rungs (5 lines at
  // n<=3, 3 lines above). Folded rows carry no description element at any count,
  // so nothing here is a `maxLines: 0` case any more — suppression by density was
  // replaced by the leader/folded role split.
  const resolvedItemCount = resolvedSidebarItems.length;
  // The flat `itemName` maxLines is 1. The rail ladder grants 2 lines from four
  // to seven concepts, so it is not Math.min'd against that 1: min would erase the rung. `ladderMaxLines` already falls
  // back to the flat maxLines when the field has no ladder. The same value
  // feeds the rail height math below, so measurement and render agree.
  const effectiveItemNameMaxLines = ladderMaxLines(
    "itemName",
    slideTypeForLimits,
    resolvedItemCount,
  );
  const effectiveDescriptionMaxLines = Math.min(
    descriptionClamp.maxLines,
    ladderMaxLines("description", slideTypeForLimits, resolvedItemCount),
  );
  const effectiveDescriptionMinLines = Math.min(
    descriptionClamp.minLines,
    effectiveDescriptionMaxLines,
  );

  // A point finds its rail row by asset id when both carry one. A deck stored
  // without it (the legacy reader) joins by a label that names exactly one row,
  // else by the payload's index alignment (the schema aligns `sidebarItems` to
  // `points`). Two concepts sharing a name never borrow each other's badge.
  const sidebarLabelIndex = useMemo(
    () => uniqueLabelIndex(resolvedSidebarItems.map((item) => item.label)),
    [resolvedSidebarItems],
  );
  const sidebarAssetIndex = useMemo(
    () => uniqueAssetIdIndex(resolvedSidebarItems),
    [resolvedSidebarItems],
  );

  const sidebarHeadline = useMemo(
    () => resolveHeadline(actionTitle),
    [actionTitle],
  );
  const hasSelectionFootnote = Boolean(
    selectionFootnote && selectionFootnote.trim(),
  );
  const expandedLeaderCap = maxExpandedLeaderRows({
    itemCount: resolvedItemCount,
    descriptionMaxLines: effectiveDescriptionMaxLines,
    nameMaxLines: effectiveItemNameMaxLines,
    hasHeadline: Boolean(sidebarHeadline),
    hasFootnote: hasSelectionFootnote,
  });
  const sidebarRows = useMemo<Array<SidebarRow>>(() => {
    // H1: the ceiling demotes by rank, not by row position. The nominees are sorted
    // furthest-first and the ceiling takes them from the front, so when it expands
    // fewer rows than the builder nominated, the ones that survive are the concepts
    // the rail's own prose claims it explains.
    //
    // A deck stored before every concept was nominated still stamps `isLeader`
    // false on the row that lost the old flat cap of three. When the measured
    // ceiling can hold every row, that flag must not hide a description the
    // rail has room to print. The flag stays the gate only once something has
    // to fold.
    const everyRowFits = expandedLeaderCap >= resolvedSidebarItems.length;
    const expandedIndexes = new Set<number>(
      resolvedSidebarItems
        .map((item, index) => ({ item, index }))
        .filter((entry) => everyRowFits || entry.item.isLeader)
        .sort((a, b) => a.item.leaderRank - b.item.leaderRank)
        .slice(0, expandedLeaderCap)
        .map((entry) => entry.index),
    );
    return resolvedSidebarItems.map((item, index) => ({
      ...item,
      isExpanded: expandedIndexes.has(index),
      foldedExplanation: item.tooltipDescription || item.description,
    }));
  }, [expandedLeaderCap, resolvedSidebarItems]);
  // Plots Wave 7 D1: the map is the quadrant plot at slide size. A slide built
  // with `plot` draws Python's stamps through `readQuadrantSlide`; one stored
  // before the stamp keeps the slide's own legacy reader, drawn as it always was.
  const stampedPlotPayload = (props as { plot?: unknown }).plot;
  const isStampedMap = quadrantSlidePlotAdapter.accepts(props);
  // A plot point and its slide point, by the item id both carry (ids that occur
  // once on each side). Before the map drew the plot, a card's name edit was
  // saved to the slide's point only, so a stored edit lives there: the map
  // draws it. Memoized on the stored payload, so placement is not redone on
  // every render.
  const stampedLink = useMemo(() => {
    if (!isStampedMap) {
      return null;
    }
    const plot = readQuadrantSlide({ ...props, plot: stampedPlotPayload });
    const slideIndexById = uniqueAssetIdIndex(points || []);
    const plotIndexById = uniqueAssetIdIndex(
      plot.data.points.map((point) => ({ assetId: point.id })),
    );
    const slideIndexOfPlot = plot.data.points.map((point) => {
      const id = usableAssetId(point.id);
      return id ? slideIndexById.get(id) : undefined;
    });
    const plotIndexOfSlide = (points || []).map((point) => {
      const id = usableAssetId(point.assetId);
      return id ? plotIndexById.get(id) : undefined;
    });
    const map = {
      ...plot,
      data: {
        ...plot.data,
        points: plot.data.points.map((point, plotIndex) => {
          const slideIndex = slideIndexOfPlot[plotIndex];
          const slideLabel =
            slideIndex !== undefined ? points[slideIndex]?.label : undefined;
          return typeof slideLabel === "string" &&
            slideLabel.trim() &&
            slideLabel !== point.label
            ? { ...point, label: slideLabel }
            : point;
        }),
      },
    };
    return { map, slideIndexOfPlot, plotIndexOfSlide };
    // The props spread only feeds the adapter's shape check; the payload and
    // the points are what the result reads.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isStampedMap, stampedPlotPayload, points]);
  const stampedMap = stampedLink?.map ?? null;
  const legacyMapPoints = useMemo(
    () =>
      isStampedMap
        ? []
        : readLegacyQuadrantMapPoints({
            displayPoints,
            resolvedSidebarItems,
            sidebarAssetIndex,
            sidebarLabelIndex,
            tooltipData,
            sourceIndexOf,
          }),
    // `sourceIndexOf` reads `points`, which `displayPoints` already follows.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
      isStampedMap,
      displayPoints,
      resolvedSidebarItems,
      sidebarAssetIndex,
      sidebarLabelIndex,
      tooltipData,
    ],
  );
  // A card's name edit writes where the map reads it: the plot's point when
  // the map draws the plot, and the slide's point as well, so every reader of
  // either agrees; the slide's point alone otherwise. The two writes are one
  // edit (one undo step) where the host records several fields at once.
  const pointLabelFieldPrefix = isStampedMap ? "plot.data.points" : "points";
  const editBothLabels = (
    plotIndex: number | undefined,
    slideIndex: number | undefined,
    value: string,
  ) => {
    if (!onEdit) {
      return;
    }
    const edits = [
      ...(plotIndex !== undefined
        ? [{ field: `plot.data.points.${plotIndex}.label`, value }]
        : []),
      ...(slideIndex !== undefined
        ? [{ field: `points.${slideIndex}.label`, value }]
        : []),
    ];
    if (onEditFields) {
      onEditFields(edits);
      return;
    }
    for (const edit of edits) {
      onEdit(edit.field, edit.value);
    }
  };
  const editStampedPointLabel = (plotIndex: number) => (_field: string, value: string) => {
    editBothLabels(plotIndex, stampedLink?.slideIndexOfPlot[plotIndex], value);
  };
  // The rail renames the same concept: on a stamped slide its edit writes both
  // places too, as one edit.
  const editRailLabel =
    isStampedMap && onEdit
      ? (slideIndex: number) => (_field: string, value: string) => {
          editBothLabels(stampedLink?.plotIndexOfSlide[slideIndex], slideIndex, value);
        }
      : null;
  const renderPointLabel = (args: QuadrantSlideMapLabelArgs) => (
    <EditableText
      value={args.point.label}
      field={`${pointLabelFieldPrefix}.${args.point.sourceIndex}.label`}
      editable={Boolean(editable && onEdit)}
      onEdit={
        isStampedMap && onEdit
          ? editStampedPointLabel(args.point.sourceIndex)
          : onEdit
      }
      style={args.style}
    />
  );
  const map = stampedMap ? (
    <QuadrantPlot
      data={stampedMap.data}
      props={{ ...stampedMap.props, renderMode }}
      variant="slide-map"
      renderPointLabel={renderPointLabel}
    />
  ) : (
    <QuadrantSlideMap
      model={{
        points: legacyMapPoints,
        xAxisLabel: formatQuadrantAxisLabel(
          (axisLabels?.x || props.xAxisLabel || "").trim(),
        ),
        yAxisLabel: formatQuadrantAxisLabel(
          (axisLabels?.y || props.yAxisLabel || "").trim(),
        ),
        // A stored slide has no plot, so no axis source text behind an (i).
        xAxisFullLabel: null,
        yAxisFullLabel: null,
        midpoint: { x: xMid, y: yMid },
        cornerLabels: quadrantLabels ?? null,
      }}
      renderMode={renderMode}
      renderPointLabel={renderPointLabel}
    />
  );

  return (
    <ChartSegmentSelectionScope>
    <SlideFrame
      eyebrowText={eyebrow}
      actionTitle={undefined}
      footerText={footerText}
      clientLogoUrl={clientLogoUrl}
      eyebrowTextTransform="none"
      contentVerticalAlign="fill"
    >
      <div
        style={{
          width: "100%",
          display: "flex",
          flexDirection: "column",
          flex: 1,
          minHeight: 0,
        }}
      >
        <div
          data-testid="quadrant-map-split-grid"
          style={{
            display: "grid",
            // The rail-wide binding (plots plan AG): the plot slot and the rail.
            gridTemplateColumns: `${PLOT_RECT.w}px ${RAIL_RECT.w}px`,
            columnGap: RAIL_RECT.x - PLOT_RECT.x - PLOT_RECT.w,
            width: "100%",
            flex: 1,
            minHeight: 0,
            alignItems: "stretch",
            overflow: "hidden",
          }}
        >
          <div
            style={{
              display: "flex",
              alignItems: "flex-start",
              minWidth: 0,
              minHeight: 0,
              maxWidth: `min(100%, ${PLOT_RECT.w}px)`,
            }}
          >
            <div
              data-testid="quadrant-map-chart"
              style={{
                position: "relative",
                width: "100%",
                height: "auto",
                maxHeight: "100%",
                minWidth: 0,
                flexShrink: 1,
                aspectRatio: `${CHART_FRAME.width} / ${CHART_FRAME.height}`,
                borderRadius: "var(--ds-quadrant-map-radius)",
                background: "var(--ds-quadrant-map-bg)",
                overflow: "hidden",
              }}
            >
              <PlotBox
                slide={props as unknown as Record<string, unknown>}
                slideType="quadrant-map"
                columns={points.map((point) => ({
                  id: point.label,
                  label: point.label,
                }))}
                title={props.title || LEGACY_TITLE}
              >
              <ChartSegmentTitleSlot
                contentPadRight={32}
                style={{
                  position: "absolute",
                  left: 16,
                  top: 12,
                  right: 80,
                  zIndex: 5,
                }}
              >
                <EditableSlideText
                  data-testid="quadrant-map-chart-title"
                  value={props.title || LEGACY_TITLE}
                  field="title"
                  editable={editable}
                  onEdit={onEdit}
                  tooltipOnTruncate
                  renderMode={renderMode}
                  minLines={1}
                  maxLines={2}
                  lineHeight="1.2"
                  style={{
                    fontSize: 14,
                    fontWeight: 600,
                    color: "var(--ds-text-primary)",
                  }}
                />
              </ChartSegmentTitleSlot>
              {map}
              </PlotBox>
            </div>
          </div>

          <div
            data-testid="quadrant-map-right-rail"
            style={{
              display: "flex",
              flexDirection: "column",
              // D2: was `flex-end`, which grows overflow off the TOP of the rail
              // (the rail's own top edge clips the headline with no scroll room —
              // `scrollTop` maxed at 33px in the measured geometry, and a printed
              // slide cannot scroll at all). `flex-start` on the rail with
              // `marginTop: "auto"` on the single inner wrapper keeps the existing
              // bottom-aligned look while n is low (the wrapper's natural height
              // is shorter than the rail, so the auto margin pushes it to the
              // bottom) and lets any overflow at high n grow DOWNWARD instead,
              // where `overflowY: auto` can actually reach it.
              //
              // `justifyContent: "safe flex-end"` was the other option raised —
              // it keeps a single flex child rather than adding a wrapper — but
              // support for the `safe`/`unsafe` alignment keywords is still
              // inconsistent across the Chromium builds used for headless PDF
              // export, and that could not be verified live in this environment.
              // `flex-start` + `marginTop: auto` is universally supported and
              // produces the same visual result, so it wins on the "verify
              // browser support" condition in the brief.
              justifyContent: "flex-start",
              padding: "20px 0",
              width: "100%",
              minWidth: 0,
              minHeight: 0,
              overflowY: "auto",
            }}
          >
            <div
              style={{
                display: "flex",
                flexDirection: "column",
                gap: "20px",
                marginTop: "auto",
                minHeight: 0,
              }}
            >
            {sidebarHeadline ? (
              <FitText
                as="h2"
                maxLines={3}
                baseFontSize={24}
                minFontSize={16}
                lineHeight={1.2}
                style={{
                  fontFamily: "var(--ds-font-display)",
                  fontWeight: "var(--ds-weight-regular)",
                  color: "var(--ds-text-display-headline)",
                  margin: 0,
                }}
              >
                {sidebarHeadline}
              </FitText>
            ) : null}
            <div
              data-testid="quadrant-map-sidebar"
              style={{
                display: "flex",
                flexDirection: "column",
                gap: "16px",
                minHeight: 0,
                // Step 18 part 2: the rows themselves are `flex-shrink: 0` (D1 above),
                // but this container was not, so an over-committed rail shrank the LIST
                // instead. Its rows then overflowed its own box and painted straight
                // over the selection footnote, which sits below it in the same column —
                // 25px of measured overlap before Step 16's capacity ceiling made that
                // state unreachable. A defect avoided by an upstream calculation is one
                // regression away from returning, so the container reports its intrinsic
                // height too and the rail's own `overflowY: auto` handles the excess.
                flexShrink: 0,
              }}
            >
              {sidebarRows.map((item) => (
                <div
                  key={`sidebar-${item.sourceIndex}`}
                  data-testid={`quadrant-map-sidebar-row-${item.badgeLabel}`}
                  style={{
                    display: "flex",
                    alignItems: "flex-start",
                    gap: "16px",
                    minHeight: 0,
                    // D1: rows default to `flex-shrink: 1`, which at n=10 with
                    // 3-line descriptions squashes each row from its intrinsic
                    // 97px (a one-line concept label; 122px with a two-line one)
                    // down to 43.9px, clipping the description mid-line with
                    // no ellipsis. The line ladder alone (below) does not prevent
                    // this — it only bounds line count, not the flex layout's
                    // right to shrink the row past its content height. Rows must
                    // report their intrinsic height; the rail's own
                    // `overflowY: auto` is what handles too many of them.
                    flexShrink: 0,
                  }}
                >
                  <div
                    style={{
                      width: "29px",
                      height: "28px",
                      minWidth: "29px",
                      borderRadius: "4px",
                      background: BADGE_STYLES.sidebar.background,
                      border: BADGE_STYLES.sidebar.border,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      color: BADGE_STYLES.sidebar.color,
                      fontSize: "12px",
                      fontWeight: "var(--ds-weight-medium)",
                    }}
                  >
                    {item.badgeLabel}
                  </div>
                  <div
                    style={{
                      display: "flex",
                      flexDirection: "column",
                      gap: "4px",
                      minWidth: 0,
                      flex: 1,
                    }}
                  >
                    <div
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: "6px",
                        minWidth: 0,
                      }}
                    >
                      {/* Rail names follow the itemName density ladder (two lines from
                          four to seven concepts, one otherwise). The nowrap clamp only
                          ellipsizes inside a width-constrained box, which is what
                          this shrinking wrapper is for; the icon beside it never
                          shrinks. */}
                      <div style={{ minWidth: 0, flex: 1 }}>
                        <EditableSlideText
                          value={item.label}
                          field={`points.${item.sourceIndex}.label`}
                          editable={editable}
                          onEdit={editRailLabel ? editRailLabel(item.sourceIndex) : onEdit}
                          tag="h3"
                          minLines={itemNameClamp.minLines}
                          maxLines={effectiveItemNameMaxLines}
                          lineHeight={itemNameClamp.lineHeight}
                          // Leaders have no info icon. Recover a truncated name
                          // through the interactive clamp tooltip; static export
                          // falls back to the native title. Folded rows without
                          // an explanation still use the native title only.
                          tooltipOnTruncate={item.isExpanded}
                          renderMode={renderMode}
                          title={
                            item.isExpanded || item.foldedExplanation
                              ? undefined
                              : item.label
                          }
                          style={{
                            fontSize: "18px",
                            fontWeight: "var(--ds-weight-medium)",
                            color: "var(--ds-quadrant-legend-concept-color)",
                            margin: 0,
                          }}
                        />
                      </div>
                      {/* D17: `isStaticRender` suppresses the icon, not just the
                          tooltip around it. A folded row in a PDF is a bare one-line
                          concept name, which is exactly what the export can deliver.

                          H-review: and a row the payload stamped no explanation for
                          gets no icon either. An info icon is a promise of an
                          explanation; opening it onto the concept name alone is a
                          worse outcome than not offering it. The full name stays
                          recoverable through the native title on the name itself
                          (below), which is a hover affordance rather than a control,
                          so it also holds in the static export. */}
                      {item.isExpanded ||
                      isStaticRender ||
                      !item.foldedExplanation ? null : (
                        <FoldedRowInfoTooltip
                          label={item.label}
                          description={item.foldedExplanation}
                          badgeLabel={item.badgeLabel}
                        />
                      )}
                    </div>
                    {/* D14: only an expanded leader row renders prose, and the guard
                        is on the element rather than on the text — a folded row has
                        no description node at all (not an empty string, not a
                        zero-height box), because its explanation lives in the
                        tooltip above. The line budget still passes through the
                        ladder, which no longer has a zero rung; the `> 0` test
                        stays as insurance against a rung being re-tightened. */}
                    {item.isExpanded &&
                    item.description &&
                    effectiveDescriptionMaxLines > 0 ? (
                      <TextClamp
                        as="p"
                        minLines={effectiveDescriptionMinLines}
                        maxLines={effectiveDescriptionMaxLines}
                        lineHeight={descriptionClamp.lineHeight}
                        tooltipOnTruncate
                        // D17: the leader row's prose still prints, but its truncation
                        // tooltip cannot open in a PDF. In `static` the clamp falls back
                        // to the native `title` instead of rendering a dead Radix
                        // trigger. Passed explicitly — `TextClamp` is a leaf shared by
                        // every slide type and must not depend on an ambient context.
                        renderMode={renderMode}
                        style={{
                          fontSize: "14px",
                          fontWeight: "var(--ds-weight-regular)",
                          color: "var(--ds-quadrant-sidebar-body)",
                          margin: 0,
                        }}
                      >
                        {item.description}
                      </TextClamp>
                    ) : null}
                  </div>
                </div>
              ))}
            </div>
            {selectionFootnote && selectionFootnote.trim() ? (
              <div
                data-testid="quadrant-map-selection-footnote"
                style={{
                  flexShrink: 0,
                }}
              >
                <TextClamp
                  as="p"
                  minLines={selectionFootnoteClamp.minLines}
                  maxLines={selectionFootnoteClamp.maxLines}
                  lineHeight={selectionFootnoteClamp.lineHeight}
                  style={{
                    margin: "0",
                    fontSize: "12px",
                    color: "var(--ds-text-muted)",
                  }}
                >
                  {selectionFootnote.trim()}
                </TextClamp>
              </div>
            ) : null}
            </div>
          </div>
        </div>
      </div>
    </SlideFrame>
    </ChartSegmentSelectionScope>
  );
}

export const QuadrantMapSlide = registerSlide(
  "quadrant-map",
  QuadrantMapSlideComponent,
);
